import { useEffect, useMemo, useRef, useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { useNavigate } from "react-router";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { streamSkillRun } from "@/lib/agenticClient";

import { PageHeader } from "../layout/PageHeader";
import { StrategyEmpty } from "./StrategyEmpty";
import { StrategyHistory } from "./StrategyHistory";
import { StrategyReportCard } from "./StrategyReportCard";
import {
  STRATEGY_PERIOD_OPTIONS,
  STRATEGY_PERIOD_STORAGE_KEY,
  STRATEGY_SECTOR_OPTIONS,
  STRATEGY_SECTORS_STORAGE_KEY,
  STRATEGY_SKILL_ID,
  STRATEGY_SKILL_MODEL,
  type RevenueReport,
  type StrategySector,
} from "./types";

// Sprint 10.x Phase B — /strategy : cockpit fondateur pour piloter la
// stratégie revenu. Liste des rapports `revenue_reports`, génération via
// skill `revenue_strategy_report` (DeepSeek), filtres période + secteurs
// persistés en localStorage.
//
// Accès admin-only (cf pattern IntegrationsPage).
// La table `revenue_reports` et le skill ne sont pas encore en prod —
// la page reste fonctionnelle (empty state) tant qu'aucune row n'existe.

const PERIOD_DEFAULT = 30;
const HISTORY_LIMIT = 10;

function readPersistedPeriod(): number {
  if (typeof window === "undefined") return PERIOD_DEFAULT;
  try {
    const raw = localStorage.getItem(STRATEGY_PERIOD_STORAGE_KEY);
    if (!raw) return PERIOD_DEFAULT;
    const n = Number.parseInt(raw, 10);
    return STRATEGY_PERIOD_OPTIONS.some((o) => o.value === n)
      ? n
      : PERIOD_DEFAULT;
  } catch {
    return PERIOD_DEFAULT;
  }
}

function readPersistedSectors(): StrategySector[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STRATEGY_SECTORS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is StrategySector =>
      STRATEGY_SECTOR_OPTIONS.includes(s as StrategySector),
    );
  } catch {
    return [];
  }
}

export const StrategyPage = () => {
  const { identity } = useGetIdentity();
  const isAdmin = !!(identity as unknown as { administrator?: boolean })
    ?.administrator;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<number>(() => readPersistedPeriod());
  const [sectors, setSectors] = useState<StrategySector[]>(() =>
    readPersistedSectors(),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSupabaseClient()
      .auth.getUser()
      .then(({ data }) => {
        if (!cancelled) setAuthUserId(data.user?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setAuthUserId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STRATEGY_PERIOD_STORAGE_KEY, String(period));
    } catch {
      // localStorage indisponible — fail silently
    }
  }, [period]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STRATEGY_SECTORS_STORAGE_KEY,
        JSON.stringify(sectors),
      );
    } catch {
      // localStorage indisponible — fail silently
    }
  }, [sectors]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const query = useGetList<RevenueReport>(
    "revenue_reports",
    {
      pagination: { page: 1, perPage: HISTORY_LIMIT },
      sort: { field: "generated_at", order: "DESC" },
      filter: { "archived_at@is": null, audience: "founder" },
    },
    { enabled: isAdmin },
  );

  const reports = useMemo(() => query.data ?? [], [query.data]);
  const latestReport = reports[0];
  const displayedReport = useMemo(
    () =>
      selectedId
        ? (reports.find((r) => r.id === selectedId) ?? latestReport)
        : latestReport,
    [reports, selectedId, latestReport],
  );

  const handleGenerate = async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setPending(true);
    setErrorMessage(null);

    let content: string | null = null;
    let metrics: Record<string, unknown> | null = null;
    let model: string | null = null;

    try {
      for await (const ev of streamSkillRun(
        STRATEGY_SKILL_ID,
        {
          period_days: period,
          audience: "founder",
          sectors,
        },
        { signal: ctrl.signal },
      )) {
        if (ev.event === "run.done") {
          const out = (
            ev.data as {
              output?: {
                content?: string;
                metrics?: Record<string, unknown>;
                model?: string;
              };
            }
          ).output;
          content = out?.content ?? null;
          metrics = out?.metrics ?? null;
          model = out?.model ?? STRATEGY_SKILL_MODEL;
        } else if (ev.event === "run.error") {
          throw new Error(
            (ev.data as { error?: string }).error ?? "skill_run_failed",
          );
        }
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const msg = (err as Error).message;
      setErrorMessage(msg);
      notify(`Génération du rapport échouée : ${msg}`, { type: "error" });
      setPending(false);
      return;
    }

    if (!content) {
      const msg = "Le rapport généré est vide.";
      setErrorMessage(msg);
      notify(msg, { type: "warning" });
      setPending(false);
      return;
    }

    try {
      await dataProvider.create("revenue_reports", {
        data: {
          generated_by: authUserId,
          period_days: period,
          audience: "founder",
          sectors,
          content,
          metrics,
          model: model ?? STRATEGY_SKILL_MODEL,
        },
      });
      setSelectedId(null);
      await query.refetch();
      notify("Rapport généré", { type: "success" });
    } catch (err) {
      notify(`Rapport généré mais non sauvegardé : ${(err as Error).message}`, {
        type: "warning",
      });
    } finally {
      setPending(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Card className="p-6 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <div>
            <p className="font-medium">Accès restreint</p>
            <p className="text-sm text-muted-foreground">
              Seuls les administrateurs peuvent consulter la stratégie revenu.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="ml-auto"
          >
            Retour
          </Button>
        </Card>
      </div>
    );
  }

  const sectorsLabel =
    sectors.length === 0
      ? "Tous les secteurs"
      : sectors.length === 1
        ? sectors[0]
        : `${sectors.length} secteurs`;

  return (
    <div className="flex flex-col gap-4 pb-8">
      <PageHeader
        title="Stratégie Revenu"
        subtitle="Cockpit fondateur — bottlenecks, segments, actions"
      >
        <div className="flex items-center gap-2">
          <Select
            value={String(period)}
            onValueChange={(v) => setPeriod(Number.parseInt(v, 10))}
          >
            <SelectTrigger
              size="sm"
              className="h-8 text-xs w-[110px]"
              aria-label="Période d'analyse"
            >
              <SelectValue placeholder="Période" />
            </SelectTrigger>
            <SelectContent>
              {STRATEGY_PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs gap-1.5"
              >
                <span className="truncate max-w-[120px]">{sectorsLabel}</span>
                <ChevronDown className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Secteurs
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {STRATEGY_SECTOR_OPTIONS.map((sector) => (
                <DropdownMenuCheckboxItem
                  key={sector}
                  checked={sectors.includes(sector)}
                  onCheckedChange={(checked) => {
                    setSectors((curr) =>
                      checked
                        ? [...curr, sector]
                        : curr.filter((s) => s !== sector),
                    );
                  }}
                  onSelect={(e) => e.preventDefault()}
                >
                  {sector}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            type="button"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={handleGenerate}
            disabled={pending}
          >
            {pending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : reports.length === 0 ? (
              <Sparkles className="size-3.5" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            {pending
              ? "Génération…"
              : reports.length === 0
                ? "Générer"
                : "Nouveau rapport"}
          </Button>
        </div>
      </PageHeader>

      {errorMessage && (
        <Card
          className="border-red-200 bg-red-50/60 dark:bg-red-950/30 dark:border-red-900/40"
          data-testid="strategy-error"
        >
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="size-4 text-red-700 dark:text-red-300 mt-0.5" />
            <div className="flex-1 text-[13px] text-foreground/90">
              <p className="font-semibold">Erreur de génération</p>
              <p className="text-muted-foreground mt-0.5">{errorMessage}</p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="gap-1.5 h-7 text-xs"
              onClick={handleGenerate}
            >
              <RefreshCw className="size-3" />
              Réessayer
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 items-start">
        <div className="min-w-0">
          {query.isPending ? (
            <Card>
              <CardContent className="p-5 flex flex-col gap-3">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-[92%]" />
                <Skeleton className="h-3 w-[78%]" />
                <Skeleton className="h-3 w-[88%]" />
                <Skeleton className="h-3 w-[72%]" />
              </CardContent>
            </Card>
          ) : pending && !displayedReport ? (
            <PendingCard />
          ) : !displayedReport ? (
            <StrategyEmpty onGenerate={handleGenerate} disabled={pending} />
          ) : (
            <>
              {pending && (
                <div className="mb-3">
                  <PendingCard compact />
                </div>
              )}
              <StrategyReportCard
                report={displayedReport}
                onRegenerate={handleGenerate}
                onArchived={() => {
                  setSelectedId(null);
                  query.refetch();
                }}
                regenerating={pending}
              />
            </>
          )}
        </div>

        <aside className="min-w-0">
          <StrategyHistory
            reports={reports}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </aside>
      </div>
    </div>
  );
};

StrategyPage.path = "/strategy";

function PendingCard({ compact = false }: { compact?: boolean }) {
  return (
    <Card
      className="border-violet-200 bg-violet-50/60 dark:bg-violet-950/30 dark:border-violet-900/40"
      data-testid="strategy-pending"
    >
      <CardContent className={compact ? "p-4" : "p-5"}>
        <div className="flex items-center gap-2 text-[13px] text-violet-800 dark:text-violet-300 mb-3">
          <Loader2 className="size-4 animate-spin" />
          <span className="font-semibold">Génération en cours…</span>
          <span className="text-muted-foreground font-normal">
            (peut prendre 15-30s)
          </span>
        </div>
        {!compact && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-[94%]" />
            <Skeleton className="h-3 w-[88%]" />
            <Skeleton className="h-3 w-[78%]" />
            <Skeleton className="h-3 w-[92%]" />
            <Skeleton className="h-3 w-[70%]" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
